import {FC, useContext, useState} from 'react';
import {Button, Icon, Loader, Modal, Popup, Text, TextInput} from '@gravity-ui/uikit';
import block from 'bem-cn-lite';
import {CircleCheck, Eye, EyeSlash, MagicWand, Xmark} from '@gravity-ui/icons';
import {AppContext} from '@app/context';
import {CApi as userGwApi} from '@services/api';
import {usePopoverAuxControls} from '@frontend/shared';
import {PopupGenerationPassword} from './components/PopupGenerationPassword/PopupGenerationPassword';
import {priceLevelSettingsStorage} from '@app/priceLevelSettings';

import './ModalChangePassword.scss';

const b = block('modal-change-password');

interface ModalChangePasswordProps {
    open: boolean;
    onClose: () => void;
    onChangePasswordSuccess?: () => void;
    positiveAction: () => void;
}

export const ModalChangePassword: FC<ModalChangePasswordProps> = ({
    open,
    onClose,
    onChangePasswordSuccess,
    positiveAction
}) => {
    const {state} = useContext(AppContext);
    const {open: openPopup, buttonRef, toggleOpen, closePopover} = usePopoverAuxControls();

    // значения инпутов
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [newPasswordConfirm, setNewPasswordConfirm] = useState('');

    // отображение пароля
    const [showCurrentPassword, setShowCurrentPassword] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showNewPasswordConfirm, setShowNewPasswordConfirm] = useState(false);

    // ошибки
    const [currentPasswordError, setCurrentPasswordError] = useState<string | undefined>();
    const [newPasswordConfirmError, setNewPasswordConfirmError] = useState<string | undefined>();

    // валидность для CircleCheck и кнопки "Сохранить"
    const [isCurrentPasswordValid, setIsCurrentPasswordValid] = useState(false);
    const [isNewPasswordValid, setIsNewPasswordValid] = useState(false);
    const [isNewPasswordConfirmValid, setIsNewPasswordConfirmValid] = useState(false);

    // флаги для поведения валидации
    const [isNewPasswordBlurred, setIsNewPasswordBlurred] = useState(false);
    const [wasConfirmInvalidOnce, setWasConfirmInvalidOnce] = useState(false); // был хоть раз "Пароли не совпадают"

    const [currentPasswordLoading, setCurrentPasswordLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    // шаг подтверждения выхода
    const [isLogoutConfirmStep, setIsLogoutConfirmStep] = useState(false);

    // правила нового пароля
    const isNewPasswordLengthValid = newPassword.length >= 6;
    const isNewPasswordDigitValid = /\d/.test(newPassword);
    const newPasswordRulesValid = isNewPasswordLengthValid && isNewPasswordDigitValid;

    // цвета li — по умолчанию обычный текст; зелёный/красный только после blur
    const defaultNoteColor = 'rgba(0, 0, 0, 0.70)';
    const noteLengthColor = !isNewPasswordBlurred
        ? defaultNoteColor
        : isNewPasswordLengthValid
          ? '#30AA6E'
          : '#E9033A';
    const noteDigitColor = !isNewPasswordBlurred
        ? defaultNoteColor
        : isNewPasswordDigitValid
          ? '#30AA6E'
          : '#E9033A';

    // invalid для 2-го инпута: после blur, есть значение и правила не выполнены
    const isNewPasswordInvalidForInput =
        isNewPasswordBlurred && !!newPassword && !newPasswordRulesValid;

    const resetState = () => {
        setCurrentPassword('');
        setNewPassword('');
        setNewPasswordConfirm('');

        setShowCurrentPassword(false);
        setShowNewPassword(false);
        setShowNewPasswordConfirm(false);

        setCurrentPasswordError(undefined);
        setNewPasswordConfirmError(undefined);

        setIsCurrentPasswordValid(false);
        setIsNewPasswordValid(false);
        setIsNewPasswordConfirmValid(false);

        setIsNewPasswordBlurred(false);
        setWasConfirmInvalidOnce(false);

        setCurrentPasswordLoading(false);
        setSubmitting(false);

        setIsLogoutConfirmStep(false);
    };

    const handleClose = () => {
        resetState();
        onClose();
    };

    // ================== blur handlers ==================
    const handleCurrentPasswordBlur = () => {
        // Ничего не ввели — ничего не проверяем
        if (!currentPassword) return;

        setCurrentPasswordError(undefined);
        setIsCurrentPasswordValid(false);
        setCurrentPasswordLoading(true);

        userGwApi()
            .login.loginCreate({
                type: 'password',
                login: state?.user?.login,
                password: currentPassword
            })
            .then(() => {
                setCurrentPasswordLoading(false);
                setIsCurrentPasswordValid(true);
            })
            .catch(() => {
                setCurrentPasswordLoading(false);
                setIsCurrentPasswordValid(false);
                setCurrentPasswordError('Неверный текущий пароль');
            });
    };

    const validateNewPasswordConfirmOnBlur = () => {
        // ничего не введено
        if (!newPasswordConfirm) {
            // если юзер уже хоть раз вводил неправильный пароль и потом стер —
            // тогда показываем "Введите новый пароль ещё раз"
            if (wasConfirmInvalidOnce) {
                setNewPasswordConfirmError('Введите новый пароль ещё раз');
            } else {
                setNewPasswordConfirmError(undefined);
            }
            setIsNewPasswordConfirmValid(false);
            return;
        }

        // введено, но не совпадает
        if (newPasswordConfirm !== newPassword) {
            setNewPasswordConfirmError('Пароли не совпадают');
            setIsNewPasswordConfirmValid(false);
            setWasConfirmInvalidOnce(true);
            return;
        }

        // всё ок
        setNewPasswordConfirmError(undefined);
        setIsNewPasswordConfirmValid(true);
    };

    const handleNewPasswordBlur = () => {
        // Ничего не ввели — не валидируем и не красим
        if (!newPassword) {
            setIsNewPasswordValid(false);
            setIsNewPasswordBlurred(false);
            return;
        }

        setIsNewPasswordBlurred(true);
        setIsNewPasswordValid(newPasswordRulesValid);

        // если уже есть значение в подтверждении — пере-проверяем его
        if (newPasswordConfirm) {
            validateNewPasswordConfirmOnBlur();
        }
    };

    const handleNewPasswordConfirmBlur = () => {
        validateNewPasswordConfirmOnBlur();
    };

    // ================== change handlers ==================
    const onChangeCurrentPassword = (e: React.ChangeEvent<HTMLInputElement>) => {
        setCurrentPassword(e.target.value);
        if (currentPasswordError) setCurrentPasswordError(undefined);
        if (isCurrentPasswordValid) setIsCurrentPasswordValid(false);
    };

    const onChangeNewPassword = (e: React.ChangeEvent<HTMLInputElement>) => {
        setNewPassword(e.target.value);

        // при новом вводе убираем валидность и скрываем подсветку до следующего blur
        if (isNewPasswordValid) setIsNewPasswordValid(false);
        if (isNewPasswordBlurred) setIsNewPasswordBlurred(false);

        // подтверждение пароля также теряет валидность
        if (isNewPasswordConfirmValid) setIsNewPasswordConfirmValid(false);
        if (newPasswordConfirmError) setNewPasswordConfirmError(undefined);
    };

    const onChangeNewPasswordConfirm = (e: React.ChangeEvent<HTMLInputElement>) => {
        setNewPasswordConfirm(e.target.value);

        // при вводе сбрасываем ошибку и валидность
        if (newPasswordConfirmError) setNewPasswordConfirmError(undefined);
        if (isNewPasswordConfirmValid) setIsNewPasswordConfirmValid(false);
    };

    // ================== submit ==================
    // Кнопка активна когда первые два инпута валидны
    const isButtonEnabled = isCurrentPasswordValid && isNewPasswordValid;

    const handleSave = () => {
        if (!isButtonEnabled || submitting) return;

        // Валидация третьего инпута по клику на кнопку
        if (!newPasswordConfirm) {
            setNewPasswordConfirmError('Введите новый пароль ещё раз');
            return;
        }

        if (newPasswordConfirm !== newPassword) {
            setNewPasswordConfirmError('Пароли не совпадают');
            return;
        }

        // Третий инпут валиден — продолжаем сохранение
        setIsNewPasswordConfirmValid(true);
        setNewPasswordConfirmError(undefined);

        setSubmitting(true);

        userGwApi()
            .changePassword.changePasswordCreate({
                currentPassword,
                newPassword,
                newPasswordConfirm
            })
            .then(() => {
                setSubmitting(false);
                setIsLogoutConfirmStep(true);
                onChangePasswordSuccess?.();
            })
            .catch(() => {
                setSubmitting(false);
            });
    };

    const handleApplyGeneratedPassword = (generatedPassword: string) => {
        // подставляем пароль в оба поля
        setNewPassword(generatedPassword);
        setNewPasswordConfirm(generatedPassword);

        const isValidByRules = generatedPassword.length >= 6 && /\d/.test(generatedPassword);

        // новый пароль: показываем, что поле уже "потрогали" и оно валидно
        setIsNewPasswordBlurred(true);
        setIsNewPasswordValid(isValidByRules);

        setNewPasswordConfirmError(undefined);
        setIsNewPasswordConfirmValid(true);
        setWasConfirmInvalidOnce(false);
    };

    return (
        <Modal open={open} className={b()} onClose={handleClose}>
            <div className={b('header')}>
                <Text
                    variant="subheader-3"
                    className={isLogoutConfirmStep ? b('header-confirm') : undefined}
                >
                    {isLogoutConfirmStep ? 'Выйти из аккаунта?' : 'Изменение пароля'}
                </Text>
                <Button size="l" view="flat" onClick={handleClose}>
                    <Icon data={Xmark} size={16} />
                </Button>
            </div>

            {!isLogoutConfirmStep && (
                <div className={b('content')}>
                    {/* ТЕКУЩИЙ ПАРОЛЬ */}
                    <TextInput
                        className={b('input')}
                        size="xl"
                        placeholder="Текущий пароль"
                        type={showCurrentPassword ? 'text' : 'password'}
                        value={currentPassword}
                        onChange={onChangeCurrentPassword}
                        onBlur={handleCurrentPasswordBlur}
                        errorMessage={currentPasswordError}
                        errorPlacement="outside"
                        validationState={currentPasswordError ? 'invalid' : undefined}
                        autoComplete="new-password"
                        endContent={
                            <>
                                {currentPasswordLoading ? (
                                    <Loader size="s" />
                                ) : (
                                    isCurrentPasswordValid && (
                                        <Icon
                                            data={CircleCheck}
                                            size={16}
                                            className={b('icon-success')}
                                        />
                                    )
                                )}
                                <Button
                                    size="l"
                                    view="flat-secondary"
                                    type="button"
                                    onClick={() => setShowCurrentPassword((prev) => !prev)}
                                >
                                    <Icon data={showCurrentPassword ? EyeSlash : Eye} size={16} />
                                </Button>
                            </>
                        }
                    />

                    {/* НОВЫЙ ПАРОЛЬ */}
                    <div className={b('new-password')}>
                        <TextInput
                            className={b('input')}
                            size="xl"
                            placeholder="Новый пароль"
                            type={showNewPassword ? 'text' : 'password'}
                            value={newPassword}
                            onChange={onChangeNewPassword}
                            onBlur={handleNewPasswordBlur}
                            errorPlacement="outside"
                            validationState={isNewPasswordInvalidForInput ? 'invalid' : undefined}
                            endContent={
                                <>
                                    {isNewPasswordValid && (
                                        <Icon
                                            data={CircleCheck}
                                            size={16}
                                            className={b('icon-success')}
                                        />
                                    )}
                                    <Button
                                        size="l"
                                        view="flat-secondary"
                                        type="button"
                                        onClick={() => setShowNewPassword((prev) => !prev)}
                                    >
                                        <Icon data={showNewPassword ? EyeSlash : Eye} size={16} />
                                    </Button>
                                </>
                            }
                            note={
                                <ul className={b('note')}>
                                    <li className={b('note-item')} style={{color: noteLengthColor}}>
                                        <Text variant="caption-2">не менее 6 символов</Text>
                                    </li>
                                    <li className={b('note-item')} style={{color: noteDigitColor}}>
                                        <Text variant="caption-2">минимум одна цифра</Text>
                                    </li>
                                </ul>
                            }
                        />

                        <Button
                            size="xl"
                            view="normal"
                            className={b('magic-button')}
                            selected={openPopup}
                            ref={buttonRef}
                            onClick={toggleOpen}
                        >
                            <Icon data={MagicWand} size={20} />
                        </Button>
                        <Popup
                            anchorRef={buttonRef}
                            onClose={closePopover}
                            open={openPopup}
                            placement="right"
                            hasArrow
                            style={{borderRadius: '8px'}}
                            modifiers={[
                                {
                                    name: 'flip',
                                    options: {
                                        fallbackPlacements: ['bottom-start'],
                                        padding: 8
                                    }
                                },
                                {
                                    name: 'preventOverflow',
                                    options: {padding: 8}
                                },
                                {
                                    name: 'shift',
                                    options: {padding: 8}
                                }
                            ]}
                        >
                            <PopupGenerationPassword
                                closePopover={closePopover}
                                onApplyPassword={handleApplyGeneratedPassword}
                            />
                        </Popup>
                    </div>

                    {/* ПОВТОР НОВОГО ПАРОЛЯ */}
                    <TextInput
                        className={b('input')}
                        size="xl"
                        placeholder="Повторите новый пароль"
                        type={showNewPasswordConfirm ? 'text' : 'password'}
                        value={newPasswordConfirm}
                        onChange={onChangeNewPasswordConfirm}
                        onBlur={handleNewPasswordConfirmBlur}
                        errorMessage={newPasswordConfirmError}
                        errorPlacement="outside"
                        validationState={newPasswordConfirmError ? 'invalid' : undefined}
                        endContent={
                            <>
                                {isNewPasswordConfirmValid && (
                                    <Icon
                                        data={CircleCheck}
                                        size={16}
                                        className={b('icon-success')}
                                    />
                                )}
                                <Button
                                    size="l"
                                    view="flat-secondary"
                                    type="button"
                                    onClick={() => setShowNewPasswordConfirm((prev) => !prev)}
                                >
                                    <Icon
                                        data={showNewPasswordConfirm ? EyeSlash : Eye}
                                        size={16}
                                    />
                                </Button>
                            </>
                        }
                    />
                </div>
            )}

            <div className={b('footer')}>
                <Button size="l" view="flat-secondary" onClick={handleClose}>
                    {isLogoutConfirmStep ? 'Нет' : 'Отмена'}
                </Button>

                {isLogoutConfirmStep ? (
                    <Button
                        size="l"
                        view="action"
                        onClick={() => {
                            handleClose();
                            positiveAction();
                            priceLevelSettingsStorage.clear();
                        }}
                        type="button"
                    >
                        Да, выйти
                    </Button>
                ) : (
                    <Button
                        size="l"
                        view="action"
                        disabled={!isButtonEnabled || submitting}
                        loading={submitting}
                        onClick={handleSave}
                        type="button"
                    >
                        Сохранить
                    </Button>
                )}
            </div>
        </Modal>
    );
};
